import { BRAND } from "@rec/shared";
import { useState, type FormEvent } from "react";
import { KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import { authClient, authErrorMessage, signIn, twoFactor } from "@/lib/auth";
import { Button, Card } from "@/components/ui";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { cn } from "@/lib/utils";

/**
 * Sign in, with the 2FA step inline.
 *
 * Better Auth answers a password sign-in with `twoFactorRedirect` when the
 * account has TOTP enabled, so the form switches to a code field rather than
 * navigating away — the user keeps their place and their typed email.
 */
/**
 * Enter the app after a successful sign-in.
 *
 * A real navigation, not a client-side route change. Better Auth's session and
 * organization hooks are module-level atoms that were populated while signed
 * out — the organization list resolved to empty and is no longer pending, so a
 * client-side transition renders the app against that stale empty result and
 * shows "No workspace yet" to someone who has one.
 *
 * A document navigation rebuilds every atom against the session cookie that now
 * exists. It cannot loop, because by this point the server has genuinely
 * created the session.
 */
function enterApp() {
  window.location.assign("/");
}

export function SignIn() {
  const { t } = useTranslation("auth");
  const [step, setStep] = useState<"password" | "totp" | "reset" | "reset-sent">(
    "password",
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onPasswordSubmit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);

    const { data, error: signInError } = await signIn.email({ email, password });

    setPending(false);

    if (signInError) {
      setError(authErrorMessage(signInError));
      return;
    }

    if (data && "twoFactorRedirect" in data && data.twoFactorRedirect) {
      setStep("totp");
      return;
    }

    enterApp();
  }

  async function onResetSubmit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);

    await authClient.requestPasswordReset({
      email,
      redirectTo: `${window.location.origin}/reset-password`,
    });

    setPending(false);
    // Always report the same thing, whether or not the account exists —
    // otherwise this form becomes a way to enumerate who has an account.
    setStep("reset-sent");
  }

  async function onCodeSubmit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);

    const verify = useBackupCode
      ? twoFactor.verifyBackupCode({ code })
      : twoFactor.verifyTotp({ code });

    const { error: verifyError } = await verify;
    setPending(false);

    if (verifyError) {
      setError(authErrorMessage(verifyError));
      setCode("");
      return;
    }

    enterApp();
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-5">
      <div className="w-full max-w-[380px]">
        <div className="mb-7 text-center">
          <h1 className="text-xl font-semibold tracking-tight text-text">{BRAND.name}</h1>
          <p className="mt-1.5 text-sm text-text-muted">
            {step === "password"
              ? t("signIn.subtitle.password")
              : step === "totp"
                ? t("signIn.subtitle.totp")
                : step === "reset"
                  ? t("signIn.subtitle.reset")
                  : t("signIn.subtitle.resetSent")}
          </p>
        </div>

        <Card className="p-6">
          {step === "password" ? (
            <form onSubmit={onPasswordSubmit} className="space-y-4">
              <Field
                label={t("signIn.email")}
                type="email"
                value={email}
                onChange={setEmail}
                autoComplete="email"
                autoFocus
                required
              />
              <Field
                label={t("signIn.password")}
                type="password"
                value={password}
                onChange={setPassword}
                autoComplete="current-password"
                required
              />

              {error ? <ErrorNote>{error}</ErrorNote> : null}

              <Button
                type="submit"
                variant="primary"
                disabled={pending}
                className="w-full"
              >
                {pending ? <Loader2 size={15} className="animate-spin" /> : null}
                {t("signIn.submit")}
              </Button>

              <button
                type="button"
                onClick={() => setStep("reset")}
                className="block w-full text-center text-[13px] text-text-muted hover:text-text"
              >
                {t("signIn.forgotPassword")}
              </button>
            </form>
          ) : step === "reset" ? (
            <form onSubmit={onResetSubmit} className="space-y-4">
              <Field
                label={t("signIn.email")}
                type="email"
                value={email}
                onChange={setEmail}
                autoComplete="email"
                autoFocus
                required
              />
              {error ? <ErrorNote>{error}</ErrorNote> : null}
              <Button type="submit" variant="primary" disabled={pending} className="w-full">
                {pending ? <Loader2 size={15} className="animate-spin" /> : null}
                {t("signIn.sendResetLink")}
              </Button>
              <button
                type="button"
                onClick={() => { setStep("password"); setError(null); }}
                className="block w-full text-center text-[13px] text-text-muted hover:text-text"
              >
                {t("signIn.backToSignIn")}
              </button>
            </form>
          ) : step === "reset-sent" ? (
            <div className="space-y-4 text-center">
              <p className="text-sm leading-relaxed text-text-muted">
                <Trans
                  t={t}
                  i18nKey="signIn.resetSent"
                  values={{ email }}
                  components={{ email: <span className="text-text" /> }}
                />
              </p>
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => { setStep("password"); setError(null); }}
              >
                {t("signIn.backToSignIn")}
              </Button>
            </div>
          ) : (
            <form onSubmit={onCodeSubmit} className="space-y-4">
              <div className="mb-1 flex items-center gap-2 text-text-muted">
                <ShieldCheck size={15} />
                <span className="text-[13px]">
                  {useBackupCode
                    ? t("signIn.totp.backupPrompt")
                    : t("signIn.totp.appPrompt")}
                </span>
              </div>

              <Field
                label={useBackupCode ? t("signIn.totp.backupLabel") : t("signIn.totp.codeLabel")}
                type="text"
                value={code}
                onChange={setCode}
                autoComplete="one-time-code"
                inputMode={useBackupCode ? "text" : "numeric"}
                autoFocus
                required
                mono
              />

              {error ? <ErrorNote>{error}</ErrorNote> : null}

              <Button
                type="submit"
                variant="primary"
                disabled={pending}
                className="w-full"
              >
                {pending ? <Loader2 size={15} className="animate-spin" /> : null}
                {t("signIn.totp.verify")}
              </Button>

              {/* A lost phone must not mean a lost workspace. */}
              <button
                type="button"
                onClick={() => {
                  setUseBackupCode(!useBackupCode);
                  setCode("");
                  setError(null);
                }}
                className="flex w-full items-center justify-center gap-1.5 text-[13px] text-text-muted hover:text-text"
              >
                <KeyRound size={13} />
                {useBackupCode ? t("signIn.totp.useApp") : t("signIn.totp.useBackupCode")}
              </button>
            </form>
          )}
        </Card>

        <div className="mt-5 flex justify-center">
          <LanguageSwitcher variant="select" />
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  mono,
  ...props
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  mono?: boolean;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "value">) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[13px] font-medium text-text">{label}</span>
      <input
        {...props}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "h-10 w-full rounded-lg border border-border bg-bg px-3",
          "text-sm text-text placeholder:text-text-faint",
          "transition-colors focus:border-accent focus:outline-none",
          mono && "font-mono tracking-[0.2em]",
        )}
      />
    </label>
  );
}

function ErrorNote({ children }: { children: React.ReactNode }) {
  return (
    <p
      role="alert"
      className="rounded-lg bg-danger-subtle px-3 py-2 text-[13px] text-danger"
    >
      {children}
    </p>
  );
}
